import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { supabase } from '@/lib/supabase';
import { openExternalUrl } from '@/utils/openUrl';
import { normalizeCui, validateCui } from '@/utils/cui';
import { readEdgeFunctionErrorData } from '@/utils/edgeFunctionError';
import { DuplicateEmailHelpLinks } from '@/components/auth/DuplicateEmailHelpLinks';
import { cuiErrorMessage, useCuiVerification } from './useCuiVerification';
import { Input } from '@/components/ui/Input/Input';
import { PhoneInput } from '@/components/ui/PhoneInput/PhoneInput';
import type { PhoneValidationResult } from '@/components/ui/PhoneInput/PhoneInput';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { PasswordValidation } from '@/components/ui/PasswordValidation/PasswordValidation';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/ui/Toast/Toast';

export type OrganizationKind = 'ngo' | 'other';

function validatePassword(password: string): string[] {
  const errors: string[] = [];
  if (password.length < 6) {
    errors.push('Password must be at least 6 characters long');
  }
  if (!/\d/.test(password)) {
    errors.push('Your password must include one number');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Your password must include one uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Your password must include one lowercase letter');
  }
  return errors;
}

const CUI_CHECK_DELAY_MS = 600;

type CreateOrganizationResponse = {
  success?: boolean;
  error?: string;
  message?: string;
};

export const OrganizationSignupForm = () => {
  const navigate = useNavigate();
  const { showToast, hideToast, toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { status: cuiStatus, verify: verifyCui, reset: resetCui } = useCuiVerification();
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('RO');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [organizationKind, setOrganizationKind] = useState<OrganizationKind | ''>('');
  const [cui, setCui] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [showValidation, setShowValidation] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [phoneError, setPhoneError] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handlePhoneValidation = (validation: PhoneValidationResult) => {
    if (!validation.isValid) {
      setPhoneError(validation.error || 'Invalid phone number');
    } else {
      setPhoneError('');
    }
  };

  const setKind = (kind: OrganizationKind) => {
    setOrganizationKind(kind);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    setError(null);
    setDuplicateEmail(false);

    if (!organizationName.trim()) {
      setError('Please enter your organization name');
      return;
    }
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    if (phoneError) {
      setError(phoneError);
      return;
    }
    if (!city.trim()) {
      setError('Please enter a city');
      return;
    }
    if (!country.trim()) {
      setError('Please enter a country');
      return;
    }
    if (!organizationKind) {
      setError('Please select an organization type');
      return;
    }
    if (!cui.trim()) {
      setError('Please enter your CUI number');
      return;
    }
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }
    const pwdErrors = validatePassword(password);
    if (pwdErrors.length > 0) {
      setError('Please fix password requirements');
      return;
    }
    if (!confirmPassword.trim()) {
      setError('Please confirm your password');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms and Conditions to create an account');
      return;
    }

    setLoading(true);
    let succeeded = false;
    try {
      // Reuse the on-blur result when it's for the same CUI; otherwise check now.
      const cuiResult =
        cuiStatus.kind === 'verified' && cuiStatus.cui === normalizeCui(cui)
          ? cuiStatus
          : await verifyCui(cui);
      if (cuiResult.kind !== 'verified') {
        setError(cuiResult.kind === 'error' ? cuiResult.message : 'Please enter your CUI number');
        return;
      }

      const { data, error: signupError } = await supabase.functions.invoke<CreateOrganizationResponse>(
        'create-organization-account',
        {
          body: {
            organizationName: organizationName.trim(),
            email: email.trim().toLowerCase(),
            password,
            phone,
            city: city.trim(),
            country: country.trim(),
            organizationKind,
            cui: cuiResult.cui,
          },
        },
      );

      const result = data ?? readEdgeFunctionErrorData(signupError);
      if (result?.error === 'EMAIL_ALREADY_EXISTS') {
        setError('An account with this email address already exists. Please log in instead.');
        setDuplicateEmail(true);
        return;
      }
      if (!data?.success) {
        setError(
          cuiErrorMessage(result?.error) ??
            result?.error ??
            result?.message ??
            'Failed to create account. Please try again.',
        );
        return;
      }

      succeeded = true;
      showToast('Organization account created! Please log in to continue.', 'success');
      setTimeout(() => {
        navigate('/?fromSignup=true', { replace: true });
      }, 2000);
    } catch (err: unknown) {
      setError(err instanceof Error && err.message ? err.message : 'An error occurred during signup');
    } finally {
      // Stay disabled after success until the redirect fires.
      if (!succeeded) setLoading(false);
    }
  };

  // Look the CUI up shortly after typing stops. Only complete CUIs that pass the
  // offline checksum reach infocui, so partial input doesn't spend lookups.
  useEffect(() => {
    if (!validateCui(cui).ok) return;
    const timer = setTimeout(() => {
      void verifyCui(cui);
    }, CUI_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [cui, verifyCui]);

  const showCui = organizationKind === 'ngo' || organizationKind === 'other';

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <Flex as="form" onSubmit={handleSubmit} w="full" direction="column" align="stretch">
        {error && (
          <Box
            w="full"
            p="3"
            bg="rgba(255,59,48,0.1)"
            color="volyx.error"
            borderRadius="lg"
            fontSize="sm"
            mb="4"
            textAlign="center"
          >
            <Box mb={duplicateEmail ? '1' : undefined}>{error}</Box>
            {duplicateEmail && <DuplicateEmailHelpLinks />}
          </Box>
        )}

        <Flex w="full" direction="column" gap="4" mb="6">
          <Input
            id="org-signup-name"
            name="organizationName"
            type="text"
            placeholder="Organization name*"
            value={organizationName}
            onChange={(e) => {
              setOrganizationName(e.target.value);
              if (error) setError(null);
            }}
            autoComplete="organization"
            required
            error={showValidation && !organizationName.trim() ? 'Organization name is required' : undefined}
          />

          <Input
            id="org-signup-email"
            name="email"
            type="email"
            placeholder="Email address*"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            autoComplete="email"
            required
            error={showValidation && !email.trim() ? 'Email is required' : undefined}
          />

          <PhoneInput
            id="org-signup-phone"
            name="phone"
            value={phone}
            onChange={(value) => {
              setPhone(value);
              if (error) setError(null);
            }}
            onCountryChange={(code) => setPhoneCountryCode(code)}
            onValidationChange={handlePhoneValidation}
            placeholder="Phone number*"
            error={phoneError || (showValidation && !phone.trim() ? 'Phone number is required' : undefined)}
            defaultCountry={phoneCountryCode}
          />

          <Flex gap="4" w="full" css={{ '& > *': { flex: 1, marginBottom: 0 } }}>
            <Input
              id="org-signup-city"
              name="city"
              type="text"
              placeholder="City*"
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                if (error) setError(null);
              }}
              autoComplete="address-level2"
              required
              error={showValidation && !city.trim() ? 'City is required' : undefined}
            />
            <Input
              id="org-signup-country"
              name="country"
              type="text"
              placeholder="Country*"
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                if (error) setError(null);
              }}
              autoComplete="country-name"
              required
              error={showValidation && !country.trim() ? 'Country is required' : undefined}
            />
          </Flex>

          <Box>
            <Text fontSize="sm" fontWeight="medium" color="volyx.text" mb="2">
              Organization type*
            </Text>
            <Flex gap="3" wrap="wrap">
              <chakra.button
                type="button"
                flex="1"
                minW="120px"
                px="4"
                py="3"
                borderRadius="xl"
                border="2px solid"
                borderColor={organizationKind === 'ngo' ? 'volyx.primary' : 'volyx.borderLight'}
                bg={organizationKind === 'ngo' ? 'volyx.primaryLight' : 'white'}
                fontWeight="semibold"
                fontSize="sm"
                cursor="pointer"
                transition="all 0.2s"
                onClick={() => setKind('ngo')}
              >
                NGO
              </chakra.button>
              <chakra.button
                type="button"
                flex="1"
                minW="120px"
                px="4"
                py="3"
                borderRadius="xl"
                border="2px solid"
                borderColor={organizationKind === 'other' ? 'volyx.primary' : 'volyx.borderLight'}
                bg={organizationKind === 'other' ? 'volyx.primaryLight' : 'white'}
                fontWeight="semibold"
                fontSize="sm"
                cursor="pointer"
                transition="all 0.2s"
                onClick={() => setKind('other')}
              >
                Other organization
              </chakra.button>
            </Flex>
            <Text fontSize="xs" color="volyx.textSecondary" mt="2">
              NGO: registered NGO, foundation, or association. Other: company, government, or educational institution.
            </Text>
          </Box>

          {showCui && (
            <Box>
              <Input
                id="org-signup-cui"
                name="cui"
                type="text"
                inputMode="numeric"
                placeholder="CUI number*"
                value={cui}
                onChange={(e) => {
                  setCui(e.target.value);
                  resetCui();
                  if (error) setError(null);
                }}
                onBlur={() => {
                  // Valid CUIs are checked while typing; on blur, just flag a bad one.
                  if (!validateCui(cui).ok) void verifyCui(cui);
                }}
                autoComplete="off"
                required
                error={
                  cuiStatus.kind === 'error'
                    ? cuiStatus.message
                    : showValidation && !cui.trim()
                      ? 'CUI is required'
                      : undefined
                }
              />
              {cuiStatus.kind === 'checking' && (
                <Text fontSize="xs" color="volyx.textSecondary" mt="1">
                  Checking CUI…
                </Text>
              )}
              {cuiStatus.kind === 'verified' && (
                <Text fontSize="xs" color="volyx.textSecondary" mt="1">
                  Registered as:{' '}
                  <Text as="span" fontWeight="semibold" color="volyx.text">
                    {cuiStatus.officialName}
                  </Text>
                  {cuiStatus.city ? `, ${cuiStatus.city}` : ''}
                </Text>
              )}
            </Box>
          )}

          <Input
            id="org-signup-password"
            name="password"
            type="password"
            placeholder="Password*"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordErrors(validatePassword(e.target.value));
              if (error) setError(null);
            }}
            autoComplete="new-password"
            required
            showPasswordToggle
            showPassword={showPassword}
            onPasswordToggle={() => setShowPassword(!showPassword)}
            error={showValidation && !password.trim() ? 'Password is required' : undefined}
          />

          <PasswordValidation password={password} errors={passwordErrors} />

          <Input
            id="org-signup-confirmPassword"
            name="confirmPassword"
            type="password"
            placeholder="Confirm password*"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (error) setError(null);
            }}
            autoComplete="new-password"
            required
            showPasswordToggle
            showPassword={showConfirmPassword}
            onPasswordToggle={() => setShowConfirmPassword(!showConfirmPassword)}
            error={
              showValidation && !confirmPassword.trim()
                ? 'Please confirm your password'
                : confirmPassword && password !== confirmPassword
                  ? 'Passwords do not match'
                  : undefined
            }
          />

          <Checkbox
            checked={acceptedTerms}
            onChange={setAcceptedTerms}
            required
            error={showValidation && !acceptedTerms ? 'You must accept the Terms and Conditions' : undefined}
            label={
              <>
                I accept the{' '}
                <chakra.a
                  href="https://www.volyx.app/terms-conditions.html"
                  color="volyx.primary"
                  fontWeight="semibold"
                  textDecoration="underline"
                  cursor="pointer"
                  _hover={{ opacity: 0.8 }}
                  onClick={(ev: React.MouseEvent) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    openExternalUrl('https://www.volyx.app/terms-conditions.html');
                  }}
                >
                  Terms and Conditions
                </chakra.a>
              </>
            }
          />
        </Flex>

        <chakra.button
          type="submit"
          w="full"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          outline="none"
          bg="volyx.primary"
          color="white"
          boxShadow="md"
          _hover={{ boxShadow: 'lg', transform: 'translateY(-2px) scale(1.02)' }}
          _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
          disabled={loading}
        >
          Create Organization Account
        </chakra.button>
      </Flex>
    </>
  );
};
